import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { brandDiscountFromRow, computeCreatorStatement, statementRange, statementSplit, type StatementBrandPayment, type StatementSplit } from "@talentkontor/domain";
import type { StaffWorkspace } from "../../../app/session";
import { useAction } from "../../../components/ui";
import { api } from "../../../data";
import { date, money, today as heuteLokal } from "../../../lib/format";
import { regularTaxLabel, smallBusinessLabel, withholdingNotePlaceholder, type DealFull, type TalentProfile } from "./model";
import type { InvoiceView } from "../../../model/invoice-view";
import { satzDesMonats } from "../rechnungen/vorbelegung";
import { payoutOpenCents, payoutParts, type PayoutPaymentRow } from "../../../model/auszahlung-teile";
import { t, currentLang } from "../../../lib/sprache";
import { kleinunternehmerNurSitzland, steuerAuswahlHinweis } from "../../../model/steuer-verantwortung";

/**
 * Ergänzungen im Creator-Profil der Agentur: Zustellstand der letzten Mail (0056), laufende Exklusivitäten (0047),
 * Steuerstatus mit Verlauf und Gültig-ab (0062) und die Abrechnung je Zeitraum für den Creator.
 */

interface MailState { address: string; state: "bounced" | "complained" | "failed" | "delivered" | "sent"; detail: string | null; at: string }

export function MailZustellung({ talentId, email, timeZone }: { talentId: string; email: string | null; timeZone: string }) {
  const q = useQuery({
    queryKey: ["mail_address_status", talentId],
    // Nur Mails an den Creator selbst; Rückläufer an Markenadressen stehen auf der Markenseite (SICHT-AGENTUR-REST-22)
    queryFn: () => api.select<MailState>("mail_address_status", { columns: "address, state, detail, at", filters: [["talent_id", "eq", talentId], ["recipient_kind", "eq", "talent"]] }).catch(() => [] as MailState[]),
  });
  const bad = (q.data ?? []).filter((m) => m.state === "bounced" || m.state === "complained" || m.state === "failed");
  if (!bad.length) return null;
  return (
    <div className="notice warning" role="status">
      {bad.map((m) => (
        <p key={m.address}>
          {m.state === "complained" ? t("Als Spam gemeldet", "Reported as spam") : m.state === "bounced" ? t("Mail zurückgekommen", "Email bounced") : t("Mail nicht zugestellt", "Email not delivered")}: {m.address} {t("am ", "on ")}{date(m.at, timeZone)}
          {m.detail ? ` · ${m.detail}` : ""}
          {email && m.address === email.trim().toLowerCase() ? t(" · Bitte die E-Mail-Adresse prüfen.", " · Please check the email address.") : ""}
        </p>
      ))}
    </div>
  );
}

/** Zustellstand der Mails an die Marke (Rechnung, Mahnung, Vertrag): Rückläufer an ihre Adressen (SICHT-AGENTUR-REST-22) */
export function MarkenMailZustellung({ brandId, timeZone, onEdit }: { brandId: string; timeZone: string; onEdit?: () => void }) {
  const q = useQuery({
    queryKey: ["mail_address_status", "brand", brandId],
    queryFn: () => api.select<MailState>("mail_address_status", { columns: "address, state, detail, at", filters: [["brand_id", "eq", brandId], ["recipient_kind", "eq", "brand"]] })
      .catch(() => [] as MailState[]),
  });
  const bad = (q.data ?? []).filter((m) => m.state === "bounced" || m.state === "complained" || m.state === "failed");
  if (!bad.length) return null;
  return (
    <div className="notice warning" role="status">
      {bad.map((m) => (
        <p key={m.address}>
          {m.state === "complained" ? t("Als Spam gemeldet", "Reported as spam") : m.state === "bounced" ? t("Mail zurückgekommen", "Email bounced") : t("Mail nicht zugestellt", "Email not delivered")}: {m.address} {t("am ", "on ")}{date(m.at, timeZone)}
          {m.detail ? ` · ${m.detail}` : ""}{t(" · Bitte die Adresse der Marke prüfen; ohne Zugang läuft keine Zahlungsfrist.", " · Please check the brand's address; without receipt no payment term runs.")}
        </p>
      ))}
      {onEdit && <button type="button" onClick={onEdit}>{t("Adresse bearbeiten", "Edit address")}</button>}
    </div>
  );
}

type DealEx = { id: string; title: string; exclusive?: boolean | null; exclusive_until?: string | null; exclusive_scope?: string | null };

export function Exklusivitaeten({ p, today, brandName }: { p: TalentProfile; today: string; brandName: (id: string | null) => string }) {
  const running = p.deals
    .map((l) => ({ l, d: l.deal as unknown as DealEx & { brand_id: string | null } }))
    .filter(({ d }) => d.exclusive && (!d.exclusive_until || d.exclusive_until >= today));
  if (!running.length) return null;
  return (
    <section className="panel stack" aria-labelledby="exklusiv-title">
      <h3 id="exklusiv-title">{t("Laufende Exklusivität", "Ongoing exclusivity")}</h3>
      {running.map(({ d }) => (
        <p key={d.id}>
          <strong>{brandName(d.brand_id)}</strong> · {d.title}
          {d.exclusive_until ? t(` · bis ${date(d.exclusive_until)}`, ` · until ${date(d.exclusive_until)}`) : t(" · ohne Enddatum", " · without end date")}
          {d.exclusive_scope ? ` · ${d.exclusive_scope}` : ""}
        </p>
      ))}
      <small>{t("Neue Kooperationen in derselben Branche fragen vor dem Speichern nach.", "New deals in the same industry ask before saving.")}</small>
    </section>
  );
}

interface TaxEntry { small_business: boolean; valid_from: string; note: string | null }

export function Steuerstatus({ ws, talentId, current, country }: { ws: StaffWorkspace; talentId: string; current: boolean; country?: string | null }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["tax_status_history", talentId],
    queryFn: () => api.select<TaxEntry>("tax_status_history", { columns: "small_business, valid_from, note", filters: [["talent_id", "eq", talentId]], order: [["valid_from", "asc"]] }).catch(() => [] as TaxEntry[]),
  });
  const { run, busy } = useAction();
  const [to, setTo] = useState<boolean>(!current);
  const [from, setFrom] = useState("");
  const [note, setNote] = useState("");
  // Begriffe nach dem Land des Creators (SAAS-R2-7): Schweiz „nicht mehrwertsteuerpflichtig“ bzw. „MWST-pflichtig“
  const label = (sb: boolean) => (sb ? smallBusinessLabel(country) : regularTaxLabel(country));
  const save = () => void run("steuer", async () => {
    await api.rpc("tax_status_set", { p_org: ws.org.org_id, p_talent: talentId, p_small_business: to, p_valid_from: from, p_note: note.trim() || null });
    setFrom("");
    setNote("");
    await qc.invalidateQueries({ queryKey: ["tax_status_history", talentId] });
  }, t("Steuerstatus eingetragen.", "Tax status entered."));
  return (
    <section className="panel stack" aria-labelledby="steuer-title">
      <h3 id="steuer-title">{t("Steuerstatus", "Tax status")}</h3>
      <p className="muted">{steuerAuswahlHinweis()}</p>
      <p>{t("Heute: ", "Today: ")}<strong>{label(current)}</strong>{t(". Belege nehmen den Stand zu ihrem Leistungs- bzw. Rechnungsdatum.", ". Documents use the status at their service or invoice date.")}</p>
      {(q.data?.length ?? 0) > 0 && (
        <ul>
          {q.data!.map((h) => (
            <li key={h.valid_from}>{h.valid_from <= "2000-01-01" ? t("Zu Beginn", "At the start") : t(`Ab ${date(h.valid_from)}`, `From ${date(h.valid_from)}`)}: {label(h.small_business)}{h.note && h.note !== "Anlage" && h.note !== "Ausgangsstand" ? ` · ${h.note}` : ""}</li>
          ))}
        </ul>
      )}
      <div className="tk-felder tk-felder-eng">
        <label>
          {t("Wechsel zu", "Change to")}<select value={to ? "ku" : "regel"} onChange={(e) => setTo(e.target.value === "ku")}>
            <option value="regel">{label(false)}</option>
            <option value="ku">{label(true)}</option>
          </select>
        </label>
        <label>
          {t("Gilt ab", "Valid from")}<input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label>
          {t("Anlass (optional)", "Occasion (optional)")}<input type="text" maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("z. B. Umsatzgrenze überschritten", "e.g. revenue threshold exceeded")} />
        </label>
      </div>
      {(to || current) && kleinunternehmerNurSitzland(country) && <p className="notice">{kleinunternehmerNurSitzland(country)}</p>}
      <div className="row"><button disabled={!from || !!busy} onClick={save}>{t("Wechsel eintragen", "Enter change")}</button></div>
      <small>{country === "CH" || country === "LI"
        ? t("Im Dezember fragt CreatorOffice nicht mehrwertsteuerpflichtige Creator nach ihrem Status fürs neue Jahr.", "In December CreatorOffice asks creators not registered for VAT about their status for the new year.")
        : t("Im Dezember fragt CreatorOffice Kleinunternehmer nach ihrem Status fürs neue Jahr.", "In December CreatorOffice asks small businesses about their status for the new year.")}</small>
    </section>
  );
}

export interface AbrechnungLine { invoice_id: string; description: string; net_cents: number; commission_free: boolean; deal_id?: string | null; period_id?: string | null }

/** Bezahlte Rechnungen an die Marke, die den Creator betreffen: eigene (Creator oder seine Kooperation) und Sammelrechnungen mit seinen Positionen. */
export function markenRechnungenDesCreators(talentId: string, views: readonly InvoiceView[], dealIds: ReadonlySet<string>): { own: InvoiceView[]; sammel: InvoiceView[] } {
  // Ein Forderungsausfall steht auf „bezahlt“, ist aber kein Eingang der Marke (GELD-5)
  const paid = views.filter((v) => v.row.kind === "agency_to_brand" && v.row.paid_at && !(v.row as { bad_debt_at?: string | null }).bad_debt_at);
  const own = paid.filter((v) => v.row.talent_id === talentId || (!!v.row.deal_id && dealIds.has(v.row.deal_id)));
  return { own, sammel: paid.filter((v) => !own.includes(v) && !v.row.talent_id && !v.row.deal_id) };
}

/**
 * Zahlungen der Marken für die Abrechnung des Creators (GELD-20): je Rechnung die Positionen des Creators (bei Sammelrechnungen
 * nur seine), je Kooperationsmonat aufgeteilt nach Satz bzw. Staffel des Monats und dem Abzug der Marke (statementSplit); die
 * Agenturgebühr gehört der Agentur, Auslagen gehen ganz an den Creator. Ohne Positionen (übernommene Belege) zählt der Nettobetrag als Honorar.
 */
export function markenZahlungen(input: {
  invoices: readonly InvoiceView[]; lines: readonly AbrechnungLine[]; deals: ReadonlyMap<string, DealFull>; periods: ReadonlyMap<string, { month: string }>;
  talentCommissionBp: number | null; agencyBp: number | null; brandName: (id: string | null) => string;
}): StatementBrandPayment[] {
  const out: StatementBrandPayment[] = [];
  for (const v of input.invoices) {
    const all = input.lines.filter((l) => l.invoice_id === v.row.id);
    // Sammelrechnung: nur die Positionen der Kooperationen dieses Creators
    const mine = v.row.deal_id ? all : all.filter((l) => !!l.deal_id && input.deals.has(l.deal_id));
    if (!v.row.deal_id && !mine.length) continue;
    const ls: AbrechnungLine[] = mine.length ? mine : [{ invoice_id: v.row.id, description: "", net_cents: v.row.net_cents ?? 0, commission_free: false }];
    const groups = new Map<string, AbrechnungLine[]>();
    for (const l of ls) {
      const k = `${l.deal_id ?? v.row.deal_id ?? ""}|${l.period_id ?? v.row.period_id ?? ""}`;
      groups.set(k, [...(groups.get(k) ?? []), l]);
    }
    const sum: StatementSplit = { netCents: 0, commissionCents: 0, feeCents: 0, expensesCents: 0, creatorShareCents: 0 };
    let percent: number | null = null;
    for (const [k, group] of groups) {
      const [dealId, periodId] = k.split("|");
      const deal = dealId ? input.deals.get(dealId) : undefined;
      const month = (periodId ? input.periods.get(periodId)?.month : null) ?? v.row.revenue_month ?? v.row.issue_date ?? null;
      const rate = deal
        ? satzDesMonats(deal as DealFull & { commission_tiers?: unknown }, month?.slice(0, 7) ?? null, input.talentCommissionBp, input.agencyBp)
        : { percent: (input.talentCommissionBp ?? input.agencyBp ?? 0) / 100, tiers: null };
      percent ??= rate.percent;
      const s = statementSplit(group.map((l) => ({ description: l.description, netCents: l.net_cents, commissionFree: l.commission_free })), rate,
        brandDiscountFromRow(deal as { brand_discount_bp?: number | null; brand_discount_bearer?: string | null } | undefined));
      for (const f of ["netCents", "commissionCents", "feeCents", "expensesCents", "creatorShareCents"] as const) sum[f] += s[f];
    }
    const firstDeal = input.deals.get(v.row.deal_id ?? [...groups.keys()][0]!.split("|")[0]!);
    out.push({ invoiceNumber: v.row.number, brand: input.brandName(v.row.brand_id ?? firstDeal?.brand_id ?? null), dealTitle: firstDeal?.title ?? null,
      paidOn: v.row.paid_at!.slice(0, 10), currency: v.row.currency, netCents: sum.netCents, commissionableCents: sum.netCents - sum.expensesCents - sum.feeCents,
      commissionPercent: percent ?? 0, split: sum });
  }
  return out;
}

export function Abrechnung({ p, today, brandName, views, periods, agencyBp }: {
  p: TalentProfile; today: string; brandName: (id: string | null) => string;
  /** Alle Rechnungen der Agentur (Sammelrechnungen an Marken haben keinen Creator) und die Monate für Satz und Staffel */
  views: readonly InvoiceView[]; periods: ReadonlyMap<string, { month: string }>; agencyBp: number | null;
}) {
  const [key, setKey] = useState(() => `${today.slice(0, 4)}-Q${Math.floor((Number(today.slice(5, 7)) - 1) / 3) + 1}`);
  const range = statementRange(key);
  const deals = new Map(p.deals.map((l) => [l.deal.id, l.deal]));
  const marke = markenRechnungenDesCreators(p.talent.id, views, new Set(deals.keys()));
  const ids = [...marke.own, ...marke.sammel].map((v) => v.row.id);
  const lines = useQuery({
    queryKey: ["invoice_lines", "abrechnung", p.talent.id, ids.join(",")],
    enabled: ids.length > 0,
    queryFn: () => api.select<AbrechnungLine>("invoice_lines", { columns: "invoice_id, description, net_cents, commission_free, deal_id, period_id", filters: [["invoice_id", "in", ids]] }),
  });
  // Gebuchte Teilauszahlungen, Verrechnungen und Einbehalte der eigenen Creator-Rechnungen (GELD-R3-4)
  const ownIds = p.invoices.filter((v) => v.row.kind === "talent_to_agency").map((v) => v.row.id);
  const parts = useQuery({
    queryKey: ["invoice_payments", "abrechnung", p.talent.id, ownIds.join(",")],
    enabled: ownIds.length > 0,
    queryFn: () => api.select<PayoutPaymentRow>("invoice_payments", { columns: "invoice_id, kind, amount_cents, paid_on, created_at", filters: [["invoice_id", "in", ownIds]] })
      .catch(() => [] as PayoutPaymentRow[]),
  });
  if (!range) return null;
  const brandPayments = markenZahlungen({ invoices: [...marke.own, ...marke.sammel], lines: lines.data ?? [], deals, periods, talentCommissionBp: p.talent.commission_bp ?? null, agencyBp, brandName });
  const own = p.invoices.filter((v) => v.row.kind === "talent_to_agency");
  const s = computeCreatorStatement({
    talentName: p.name, from: range.from, to: range.to, brandPayments,
    // Auszahlung am Tag der Buchung bzw. des Vermerks (wie die KSK), Teilbeträge einzeln; offen ist der Rest (GELD-R3-4)
    payouts: own.flatMap((v) => payoutParts(v.row, parts.data ?? []).map((x) => ({ invoiceNumber: v.row.number, paidOn: x.paidOn, currency: v.row.currency, grossCents: x.cents }))),
    openPayouts: own.filter((v) => v.row.status === "approved" || v.row.status === "sent")
      .map((v) => ({ invoiceNumber: v.row.number, label: v.row.revenue_month ? v.row.revenue_month.slice(0, 7) : "", dueDate: v.row.effective_due_date ?? null, currency: v.row.currency, grossCents: payoutOpenCents(v.row, parts.data ?? []) }))
      .filter((o) => o.grossCents > 0),
    commissionInvoices: p.invoices.filter((v) => v.row.kind === "agency_to_talent" && v.row.issue_date)
      .map((v) => ({ invoiceNumber: v.row.number, issueDate: v.row.issue_date!, currency: v.row.currency, grossCents: v.row.gross_cents ?? 0, paid: !!v.row.paid_at })),
    language: currentLang(),
  });
  const withFee = s.totals.some((x) => x.feeCents !== 0);
  const y = Number(today.slice(0, 4));
  const options = [...[y, y - 1].flatMap((yy) => [`${yy}`, `${yy}-Q4`, `${yy}-Q3`, `${yy}-Q2`, `${yy}-Q1`])];
  const labelOf = (k: string) => (/-Q\d$/.test(k) ? `${k.slice(-2)} ${k.slice(0, 4)}` : t(`Jahr ${k}`, `Year ${k}`));
  return (
    <section className="panel stack tk-abrechnung" aria-labelledby="abrechnung-title">
      <div className="row between">
        <h3 id="abrechnung-title">{t("Abrechnung für ", "Statement for ")}{p.name}</h3>
        <div className="row">
          <select aria-label={t("Zeitraum", "Period")} value={key} onChange={(e) => setKey(e.target.value)}>
            {options.map((k) => <option key={k} value={k}>{labelOf(k)}</option>)}
          </select>
          <button onClick={() => window.print()}>{t("Drucken oder als PDF sichern", "Print or save as PDF")}</button>
        </div>
      </div>
      <p className="muted">{date(range.from)} {t("bis ", "until ")}{date(range.to)} {t("· Zahlungen der Marken (Weg über die Agentur), Provision, Auslagen, Auszahlungen und offene Posten. Die Aufstellung sehen nur Personen mit dem Recht „Beträge sehen“.", "· brand payments (via the agency), commission, expenses, payouts and open items. Only people with the “View amounts” permission see this statement.")}</p>
      {s.totals.length === 0 && s.open.length === 0 ? <p>{t("Im Zeitraum gibt es keine Zahlungen.", "There are no payments in the period.")}</p> : (
        <div className="tablewrap tk-cardtable">
          <table>
            <caption className="sr-only">{t("Summen je Währung", "Totals per currency")}</caption>
            <thead><tr><th scope="col">{t("Währung", "Currency")}</th><th scope="col" className="money">{t("Eingang Marken netto", "Brand receipts net")}</th><th scope="col" className="money">{t("Provision", "Commission")}</th>{withFee && <th scope="col" className="money">{t("Agenturgebühr", "Agency fee")}</th>}<th scope="col" className="money">{t("Auslagen", "Expenses")}</th><th scope="col" className="money">{t("Anteil Creator netto", "Creator share net")}</th><th scope="col" className="money">{t("Ausgezahlt brutto", "Paid out gross")}</th><th scope="col" className="money">{t("Offen brutto", "Open gross")}</th></tr></thead>
            <tbody>
              {s.totals.map((tl) => (
                <tr key={tl.currency}>
                  <td data-label={t("Währung", "Currency")}>{tl.currency}</td>
                  <td data-label={t("Eingang Marken netto", "Brand receipts net")} className="num">{money(tl.brandNetCents, tl.currency)}</td>
                  <td data-label={t("Provision", "Commission")} className="num">{money(tl.commissionCents, tl.currency)}</td>
                  {withFee && <td data-label={t("Agenturgebühr", "Agency fee")} className="num">{money(tl.feeCents, tl.currency)}</td>}
                  <td data-label={t("Auslagen", "Expenses")} className="num">{money(tl.expensesCents, tl.currency)}</td>
                  <td data-label={t("Anteil Creator netto", "Creator share net")} className="num">{money(tl.creatorShareCents, tl.currency)}</td>
                  <td data-label={t("Ausgezahlt brutto", "Paid out gross")} className="num">{money(tl.paidOutCents, tl.currency)}</td>
                  <td data-label={t("Offen brutto", "Open gross")} className="num">{money(tl.openCents, tl.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {s.lines.length > 0 && (
        <ul>
          {s.lines.map((l, i) => <li key={i}>{date(l.date)} · {l.text} · <span className="num">{money(l.cents, l.currency)}</span></li>)}
        </ul>
      )}
    </section>
  );
}

/** Creator im Ausland: Freistellung von der Abzugsteuer (0063); die Warnung erscheint vor der Auszahlung. */
export function Abzugsteuer({ ws, talent }: { ws: StaffWorkspace; talent: { id: string; country: string | null; withholding_exemption_until?: string | null; withholding_exemption_note?: string | null } }) {
  const org = (ws.org.country ?? "").toUpperCase();
  const creator = (talent.country ?? "").toUpperCase();
  const qc = useQueryClient();
  const { run, busy } = useAction();
  const [until, setUntil] = useState(talent.withholding_exemption_until ?? "");
  const [note, setNote] = useState(talent.withholding_exemption_note ?? "");
  if (!["DE", "AT"].includes(org) || !creator || creator === org) return null;
  const law = org === "DE" ? "§ 50a EStG" : "§ 99 EStG";
  const valid = !!talent.withholding_exemption_until && talent.withholding_exemption_until >= heuteLokal();
  return (
    <section className="panel stack" aria-labelledby="abzug-title">
      <h3 id="abzug-title">{t("Abzugsteuer (Creator im Ausland)", "Withholding tax (creators abroad)")}</h3>
      <p><strong>{t("Achtung: ", "Note: ")}</strong>{t("Zahlst du diesem Creator Geld für Nutzungsrechte (zum Beispiel Werbung mit seinem Video: Paid Usage, Whitelisting, Spark Ads, Buyout), musst du ohne Freistellungsbescheinigung einen Teil als Steuer einbehalten und selbst ans Finanzamt zahlen (", "If you pay this creator for usage rights (for example advertising with their video: paid usage, whitelisting, Spark Ads, buyout), you must withhold part of it as tax and pay it to the tax office yourself unless there is an exemption certificate (")}{law}{t("); sonst haftest du selbst. Die Freistellung beantragt der Creator bei der Steuerbehörde (in Deutschland beim Bundeszentralamt für Steuern). Frag im Zweifel deine Steuerberatung und trag hier ein, bis wann die Bescheinigung gilt; der Auszahlungslauf weist dann darauf hin.", "); otherwise you are liable yourself. The creator applies for the exemption with the tax authority (in Germany the Federal Central Tax Office). If in doubt, ask your tax advisor and enter here until when the certificate is valid; the payout run then points this out.")}</p>
      <p>{valid ? <span className="pill success"><span className="pill-zeichen">✓</span> {t("Freistellung gültig bis ", "Exemption valid until ")}{date(talent.withholding_exemption_until!)}</span> : <span className="pill warning"><span className="pill-zeichen">◷</span> {t("Keine gültige Freistellung hinterlegt", "No valid exemption on file")}</span>}</p>
      <div className="grid">
        <label>{t("Freistellung gültig bis", "Exemption valid until")}<input type="date" value={until} onChange={(e) => setUntil(e.target.value)} /></label>
        <label>{t("Notiz (optional)", "Note (optional)")}<input type="text" maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} placeholder={withholdingNotePlaceholder(org)} /></label>
      </div>
      <div className="row">
        <button disabled={!!busy} onClick={() => void run("abzug", async () => {
          await api.update("talents", [["id", "eq", talent.id]], { withholding_exemption_until: until || null, withholding_exemption_note: note.trim() || null });
          await qc.invalidateQueries();
        }, t("Angaben zur Abzugsteuer gespeichert.", "Withholding tax details saved."))}>{t("Speichern", "Save")}</button>
      </div>
    </section>
  );
}
